import React from 'react';
import { useRevalidator } from 'react-router-dom';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';
import {
  EmailAmsLogDestination,
  IntegrationType,
  type EmailAmsLogPreferenceInput,
  type IntegrationDetailFieldsFragment,
  useEmailAmsLogOptionsQuery,
  useSetIntegrationEmailLogPreferenceMutation
} from '~/api/graphql';

const savedPreferenceDurationMs = 2_000;
const saveFailure = 'Couldn’t save. The previous preference is still in use. Try again.';

const preferenceKeyOf = (preference: EmailAmsLogPreferenceInput) =>
  JSON.stringify({
    destination: preference.destination,
    activityCode: preference.activityCode || null,
    companyCode: preference.companyCode || null,
    assignToEmployeeCode: preference.assignToEmployeeCode || null,
    assignedByEmployeeCode: preference.assignedByEmployeeCode || null
  });

export function useEmailLogPreference({
  integration,
  canUpdate,
  onSavingChange
}: {
  integration: IntegrationDetailFieldsFragment;
  canUpdate: boolean;
  onSavingChange: (saving: boolean) => void;
}) {
  const revalidator = useRevalidator();
  const destinations = integration.emailLogDestinations ?? [];
  const storedPreference: EmailAmsLogPreferenceInput = integration.emailLogPreference
    ? {
        destination: integration.emailLogPreference.destination,
        activityCode: integration.emailLogPreference.activityCode,
        companyCode: integration.emailLogPreference.companyCode,
        assignToEmployeeCode: integration.emailLogPreference.assignToEmployeeCode,
        assignedByEmployeeCode: integration.emailLogPreference.assignedByEmployeeCode
      }
    : { destination: destinations[0] ?? EmailAmsLogDestination.Note };
  const preferenceKey = preferenceKeyOf(storedPreference);
  const [preference, setPreference] = React.useState(storedPreference);
  const previousPreference = React.useRef(storedPreference);
  const [error, setError] = React.useState<string>();
  const [saved, setSaved] = React.useState(false);
  const savedTimer = React.useRef<ReturnType<typeof setTimeout>>();
  React.useEffect(() => {
    previousPreference.current = JSON.parse(preferenceKey) as EmailAmsLogPreferenceInput;
    setPreference(previousPreference.current);
  }, [integration.id, preferenceKey]);
  React.useEffect(() => () => clearTimeout(savedTimer.current), []);
  const options = useEmailAmsLogOptionsQuery({
    variables: { id: integration.id },
    skip: !canUpdate
  });
  const [savePreference] = useSetIntegrationEmailLogPreferenceMutation();
  const needsActivityCode =
    integration.type === IntegrationType.AmsAppliedEpic &&
    preference.destination === EmailAmsLogDestination.Activity;
  const needsSuspenseCodes = preference.destination === EmailAmsLogDestination.Suspense;
  const update = (next: EmailAmsLogPreferenceInput) => {
    setPreference(next);
    setSaved(false);
    setError(undefined);
  };
  const hasChanges = preferenceKeyOf(preference) !== preferenceKeyOf(previousPreference.current);
  const save = async () => {
    if (!hasChanges) return;
    if (
      (needsActivityCode && !preference.activityCode) ||
      (needsSuspenseCodes &&
        (!preference.companyCode ||
          !preference.assignToEmployeeCode ||
          !preference.assignedByEmployeeCode))
    ) {
      setError('Choose the required fields before saving.');
      return;
    }
    onSavingChange(true);
    setError(undefined);
    setSaved(false);
    clearTimeout(savedTimer.current);
    try {
      const result = await savePreference({ variables: { id: integration.id, input: preference } });
      if (messageOfFailure(result, saveFailure)) throw new Error(saveFailure);
      previousPreference.current = preference;
      setSaved(true);
      savedTimer.current = setTimeout(() => setSaved(false), savedPreferenceDurationMs);
    } catch {
      setPreference(previousPreference.current);
      setError(saveFailure);
    } finally {
      onSavingChange(false);
      void revalidator.revalidate();
    }
  };
  return {
    hasChanges,
    preference,
    destinations,
    options,
    needsActivityCode,
    needsSuspenseCodes,
    update,
    save,
    error,
    saved
  };
}
